"use client";

import { gql, useApolloClient } from "@apollo/client";
import { useCallback } from "react";
import type { GifMediaType, GiphyFetchPage, GiphyItem, GiphyPage } from "./giphy";

/**
 * Apollo client for the Giphy picker. Hand-written (not codegen'd) so the
 * feature ships without a codegen run against the live BE — the matching
 * operations live in `messaging.graphql` for when codegen next runs. The
 * widget talks ONLY to our BE proxy (`visitorGifSearch`/`visitorGifTrending`),
 * never Giphy directly; on pick, `uploadVisitorMediaFromUrl` re-hosts the asset
 * into our media store and returns a mediaId for the existing send path.
 *
 * `errorPolicy: "none"` here (overriding the app default "all") so a BE/Giphy
 * failure REJECTS — the picker shows its retry state instead of an empty grid.
 */

const ITEM_FIELDS = `
  items {
    id
    title
    previewUrl
    previewWidth
    previewHeight
    sourceUrl
    mimetype
  }
  nextOffset
  hasNext
`;

const SEARCH = gql`
  query VisitorGifSearch($q: String!, $type: GifMediaType!, $offset: Int, $limit: Int) {
    visitorGifSearch(q: $q, type: $type, offset: $offset, limit: $limit) {
      ${ITEM_FIELDS}
    }
  }
`;

const TRENDING = gql`
  query VisitorGifTrending($type: GifMediaType!, $offset: Int, $limit: Int) {
    visitorGifTrending(type: $type, offset: $offset, limit: $limit) {
      ${ITEM_FIELDS}
    }
  }
`;

const INGEST = gql`
  mutation UploadVisitorMediaFromUrl($sourceUrl: String!, $kind: GifIngestKind!) {
    uploadVisitorMediaFromUrl(sourceUrl: $sourceUrl, kind: $kind) {
      url
      mediaId
      mimetype
      size
    }
  }
`;

interface GiphyResultShape {
  items: GiphyItem[];
  nextOffset: number | null;
  hasNext: boolean;
}

/** The BE `VisitorUploadResult` — same shape as `uploadVisitorMedia`. */
export interface GiphyIngestResult {
  url: string;
  mediaId: string;
  mimetype: string;
  size: number;
}

/** Whether to store a picked asset as a plain image (GIF) or a STICKER
 *  (transparent). Mirrors the BE `GifIngestKind` enum. */
export type GifIngestKind = "IMAGE" | "STICKER";

export interface UseGiphy {
  /** Search (non-empty query) or trending (empty query), paged by offset. */
  fetchPage: GiphyFetchPage;
  /** Re-host a picked asset into our media store → returns a mediaId. */
  ingest: (sourceUrl: string, kind: GifIngestKind) => Promise<GiphyIngestResult>;
}

/** Re-host a picked Giphy asset into our media store → returns a mediaId. Split
 *  out so the shell's outbox `run` can call it at drain time (the optimistic
 *  bubble shows the Giphy preview immediately; the ingest happens in the
 *  background, like a pending byte upload). */
export function useGiphyIngest(): (
  sourceUrl: string,
  kind: GifIngestKind,
) => Promise<GiphyIngestResult> {
  const client = useApolloClient();
  return useCallback(
    async (sourceUrl: string, kind: GifIngestKind): Promise<GiphyIngestResult> => {
      const res = await client.mutate<{ uploadVisitorMediaFromUrl: GiphyIngestResult }>({
        mutation: INGEST,
        variables: { sourceUrl, kind },
        errorPolicy: "none",
      });
      const out = res.data?.uploadVisitorMediaFromUrl;
      if (!out?.mediaId) throw new Error("giphy ingest: empty response");
      return out;
    },
    [client],
  );
}

export function useGiphy(): UseGiphy {
  const client = useApolloClient();

  const fetchPage = useCallback<GiphyFetchPage>(
    async ({ type, query, offset }): Promise<GiphyPage> => {
      const isSearch = query.length > 0;
      const res = await client.query<Record<string, GiphyResultShape>>({
        query: isSearch ? SEARCH : TRENDING,
        variables: isSearch ? { q: query, type, offset } : { type, offset },
        // network-only (NOT cache-first): the `sourceUrl` a GiphyItem carries can
        // go stale (the BE re-selects renditions server-side), and a cached item
        // would then hand the ingest an outdated/oversized URL. So always fetch
        // fresh metadata — speed comes from the BE's own short cache, and the gif
        // IMAGE bytes are cached by the browser (HTTP), which is what matters for
        // scroll/reopen smoothness.
        fetchPolicy: "network-only",
        errorPolicy: "none",
      });
      const r = res.data[isSearch ? "visitorGifSearch" : "visitorGifTrending"];
      return { items: r.items, nextOffset: r.nextOffset ?? null, hasNext: r.hasNext };
    },
    [client],
  );

  const ingest = useGiphyIngest();

  return { fetchPage, ingest };
}

/** Map the picker's tab type to the ingest kind (sticker stays transparent). */
export function ingestKindFor(type: GifMediaType): GifIngestKind {
  return type === "STICKER" ? "STICKER" : "IMAGE";
}
